import type { AiChatColumn, AiChatTable, AiChatTableRow } from './types';

/** What a cell the row does not carry renders as. */
export const MISSING_CELL = '—';

/**
 * A bracketed directive a column label may end with to ask for right alignment,
 * e.g. `"Total (right)"`.
 *
 * This is the *only* signal the panel accepts, and it is deliberately narrow.
 * The obvious alternative — right-align a column whose cells all look like
 * numbers — is a guess, and a harmful one: job numbers, postcodes, years and
 * telephone extensions are all digits, and a column that changes alignment from
 * answer to answer cannot be scanned. A column that does not say gets the
 * default, which is left.
 */
const ALIGNMENT_DIRECTIVE = /\s*\((right|numeric|number)\)$/i;

/** Where a column's cells sit, and the label with any alignment directive removed. */
export interface ColumnHeading {
  /** The label to show. */
  readonly label: string;
  /** `end` right-aligns the column; `start` is the default for everything else. */
  readonly align: 'start' | 'end';
}

/**
 * Reads one column heading.
 *
 * @param label the label the server sent
 * @returns the label to show and its alignment
 */
export function columnHeading(label: string): ColumnHeading {
  const directive = ALIGNMENT_DIRECTIVE.exec(label);
  if (directive === null) {
    return { label, align: 'start' };
  }
  return { label: label.slice(0, directive.index).trimEnd(), align: 'end' };
}

/**
 * The columns that can be rendered.
 *
 * The parameter is `unknown` on purpose. This is data that crossed a wire from
 * a language model by way of the server, and the type above is a claim about it
 * rather than a fact; every check here earns its keep at runtime. A duplicate
 * key is dropped rather than allowed to shadow the first, because two columns
 * claiming one cell is a table nobody can read.
 */
function usableColumns(columns: unknown): AiChatColumn[] {
  if (!Array.isArray(columns)) return [];
  const seen = new Set<string>();
  const usable: AiChatColumn[] = [];
  for (const column of columns as unknown[]) {
    if (typeof column !== 'object' || column === null) continue;
    const candidate = column as Partial<AiChatColumn>;
    if (typeof candidate.key !== 'string' || candidate.key === '') continue;
    if (seen.has(candidate.key)) continue;
    seen.add(candidate.key);
    usable.push({
      key: candidate.key,
      label: typeof candidate.label === 'string' ? candidate.label : candidate.key,
    });
  }
  return usable;
}

/** The rows that can be rendered. A row that is not an object is not a row. */
function usableRows(rows: unknown): AiChatTableRow[] {
  if (!Array.isArray(rows)) return [];
  return (rows as unknown[]).filter(
    (row): row is AiChatTableRow => typeof row === 'object' && row !== null && !Array.isArray(row),
  );
}

/**
 * One cell.
 *
 * The contract says every cell is a formatted string. A payload that says
 * otherwise is not coerced into looking right — an em dash says "this row
 * carried nothing here", which is true, where `[object Object]` would be a
 * rendering of something nobody wrote.
 */
function cell(row: AiChatTableRow, key: string): string {
  const value: unknown = row[key];
  return typeof value === 'string' ? value : MISSING_CELL;
}

export interface AssistantTableProps {
  /** The table the answer carried. */
  readonly table: AiChatTable;
}

/**
 * A table from an assistant answer, sized for the assistant panel.
 *
 * It is a real `<table>` with a real `<caption>` and `<th scope="col">` headers,
 * so the structure survives being read aloud rather than only being looked at.
 * Cells are looked up by column key, never by position: an answer that emits its
 * row objects in a different key order, or omits a key entirely, still lines up
 * with its own headings.
 *
 * A payload whose columns are missing or unreadable renders nothing at all. A
 * table with half its headings is worse than no table — it looks authoritative
 * while being offset, and the reader has no way to tell.
 *
 * @param props see {@link AssistantTableProps}
 * @returns the table, or nothing when the payload cannot be rendered coherently
 */
export function AssistantTable({ table }: AssistantTableProps): React.JSX.Element | null {
  // Widened back to "whatever actually arrived": the prop type describes what
  // the server promises, and this is where the promise is checked.
  const payload = table as Partial<AiChatTable> | null | undefined;
  const columns = usableColumns(payload?.columns);
  if (columns.length === 0) return null;
  const rows = usableRows(payload?.rows);

  const title = typeof payload?.title === 'string' ? payload.title.trim() : '';
  const note = typeof payload?.caption === 'string' ? payload.caption.trim() : '';
  const name = title === '' ? (note === '' ? 'Answer table' : note) : title;
  const headings = columns.map((column) => ({ key: column.key, ...columnHeading(column.label) }));

  return (
    // The drawer is narrow and a wide answer is common, so the table scrolls
    // sideways inside its own focusable region rather than stretching the
    // transcript — and rather than being clipped where nobody can reach it.
    <div className="fo-answer-table__scroll" role="region" tabIndex={0} aria-label={name}>
      <table className="fo-answer-table">
        <caption className="fo-answer-table__caption">
          {title === '' ? null : <span className="fo-answer-table__title">{title}</span>}
          {note === '' ? null : <span className="fo-answer-table__note">{note}</span>}
          {title === '' && note === '' ? (
            <span className="visually-hidden">Answer table</span>
          ) : null}
        </caption>
        <thead>
          <tr>
            {headings.map((heading) => (
              <th key={heading.key} scope="col" data-align={heading.align}>
                {heading.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="fo-answer-table__empty" colSpan={headings.length}>
                The answer carried no rows.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={String(index)}>
                {headings.map((heading) => (
                  <td key={heading.key} data-align={heading.align}>
                    {cell(row, heading.key)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
